import {
  cachedTrailIsStale,
  fetchTrailPoint,
  fetchTrailVersion,
  HydratedStory,
  PointApiResponse,
  pointApiResponseSchema,
  ReducedTrail,
  reconcileStoryPoints,
} from 'magical-trails';
import { api } from 'magical-trails-backend/convex/_generated/api';

import store from '../store/index';
import { revalidateCurrentTrail } from '../store/trail';
import { API_BASE_URL } from './apiBase';
import { convex } from '../lib/convexClient';
import { saveTrail } from './localStorage';
import hydrateStory from './hydrateStory';
import setStoryPointsStatus from './set-story-points-status';

/**
 * How long a player is left on stale content, worst case:
 *
 *  - Every fresh load and every cached load asks for the live version once.
 *  - While the trail is open, the app re-asks when the tab becomes visible
 *    again, when the device comes back online, and every
 *    LIVE_TRAIL_CHECK_INTERVAL_MS (see hooks/useLiveTrailUpdates).
 *  - No two of those checks can fire within MIN_CHECK_GAP_MS of each other,
 *    and only one check per code is ever in flight.
 *
 * The probe itself (/api/trail-version) carries a short CDN TTL, so an
 * author's edit reaches a player on their next check, not a day later like
 * the stale-while-revalidate'd point payload.
 */
export const MIN_CHECK_GAP_MS = 20 * 1000;

export type SyncTrailResult = 'current' | 'updated' | 'skipped';

// Per-code single-flight + rate limiting. Three independent triggers
// (load, visibility/online, interval) can easily coincide; without these a
// backgrounded phone waking up would fire several identical round trips.
const inFlightByCode = new Map<string, Promise<SyncTrailResult>>();
const lastCheckStartedAt = new Map<string, number>();

/** Clears the single-flight + rate-limit state. Tests only. */
export function resetLiveTrailUpdatesForTests(): void {
  inFlightByCode.clear();
  lastCheckStartedAt.clear();
}

/**
 * Authed fallback for invite-only trails. Returns the trail payload when the
 * signed-in player holds a claim, 'inviteRequired' when they don't (or
 * aren't signed in), null when the code is genuinely unknown (or Convex is
 * not configured — indistinguishable from unknown for this client), and
 * 'error' when resolution FAILED (network/validation) so callers don't
 * report a real failure as an invalid code.
 */
export async function fetchInviteOnlyPoint(
  code: string
): Promise<PointApiResponse | 'inviteRequired' | 'error' | null> {
  if (!convex) return null;
  try {
    const result = await convex.query(api.points.resolveByCodeForPlayer, {
      code,
    });
    if (result === null) return null;
    if ('status' in result && result.status === 'inviteRequired') {
      return 'inviteRequired';
    }
    const validation = pointApiResponseSchema.safeParse(result);
    if (!validation.success) {
      console.error('Invite-only payload failed validation', validation.error);
      return 'error';
    }
    return validation.data;
  } catch (err) {
    console.error('Invite-only resolution failed', err);
    return 'error';
  }
}

/**
 * Carry the player's progress from their saved story onto the freshly
 * hydrated one (see `reconcileStoryPoints` in the shared package for the
 * rules), then re-run the unlock pass and keep the live session identity so
 * the swap is invisible to analytics.
 */
export function mergeStoryProgress(
  freshStory: HydratedStory,
  savedStory: HydratedStory
): HydratedStory {
  const { updatedStoryPoints } = setStoryPointsStatus(
    reconcileStoryPoints(
      freshStory.storyPoints,
      savedStory.storyPoints,
      freshStory.config
    ),
    freshStory.config
  );

  return {
    ...freshStory,
    storyPoints: updatedStoryPoints,
    trailSessionToken: savedStory.trailSessionToken,
    startedAt: savedStory.startedAt,
  };
}

type LiveProbe =
  | {
      ok: true;
      version: number;
      /**
       * Set only on the invite-only path, where resolving the version and
       * resolving the content are the same authed query — the public point
       * route 404s for these trails, so there is nothing to re-fetch.
       */
      payload?: PointApiResponse;
    }
  | { ok: false };

async function probeLiveTrail(code: string): Promise<LiveProbe> {
  const live = await fetchTrailVersion(code, { baseUrl: API_BASE_URL });
  if (live.ok) return { ok: true, version: live.data.version };
  if (live.reason !== 'notFound') return { ok: false };

  // A 404 from the public probe is also what an invite-only trail looks
  // like, so those players get live updates too — but only when this build
  // actually has a Convex client to ask.
  if (!convex) return { ok: false };
  const authed = await fetchInviteOnlyPoint(code);
  if (authed === null || authed === 'error' || authed === 'inviteRequired') {
    return { ok: false };
  }
  return { ok: true, version: authed.trail.version, payload: authed };
}

async function runSync(code: string): Promise<SyncTrailResult> {
  const probe = await probeLiveTrail(code);
  if (!probe.ok) return 'skipped';

  const loadedTrail = store.getState().trail.currentTrail;
  if (!loadedTrail) return 'skipped';
  if (!cachedTrailIsStale(loadedTrail.version, probe.version)) return 'current';

  let freshTrail: ReducedTrail;
  if (probe.payload) {
    freshTrail = probe.payload.trail;
  } else {
    // `version` busts the point route's day-long stale-while-revalidate
    // window, which is exactly what the probe just proved we are stuck in.
    const fetched = await fetchTrailPoint(code, {
      baseUrl: API_BASE_URL,
      version: probe.version,
    });
    if (!fetched.ok) return 'skipped';
    freshTrail = fetched.data.trail;
  }

  // Replace the cached trail so every future load/scan serves fresh content.
  saveTrail(freshTrail);

  // The player may have loaded a DIFFERENT trail while this was in flight.
  // Only hot-swap if the trail we just refreshed is still the active one —
  // otherwise we'd merge this trail's fresh content onto an unrelated
  // on-screen trail. (id is stable across a version bump, so fresh/cached
  // ids match; the guard is against a mid-flight trail switch.)
  const activeTrail = store.getState().trail;
  if (activeTrail.currentTrail?.id !== freshTrail.id) return 'skipped';

  const currentStory = activeTrail.currentStory;
  if (!currentStory) return 'skipped';

  // The author deleted the story the player is on. There is nothing to swap
  // in, and yanking them out of a trail they are halfway through would be
  // worse than letting them finish the copy they already have.
  const freshStory = hydrateStory(freshTrail, currentStory.code);
  if (!freshStory) return 'skipped';

  store.dispatch(
    revalidateCurrentTrail({
      trail: freshTrail,
      story: mergeStoryProgress(freshStory, currentStory),
    })
  );
  return 'updated';
}

/**
 * Ask whether the trail the player is on has been republished since it was
 * loaded and, if so, refresh the cache and hot-swap the on-screen story with
 * their progress carried over.
 *
 * Callers fire this and forget it (`void syncTrailIfStale(code)`), so it
 * never throws and never disturbs what is already on screen: every failure
 * class — offline, unknown code, a deleted story — resolves to 'skipped' and
 * leaves the served copy standing.
 */
export function syncTrailIfStale(code: string): Promise<SyncTrailResult> {
  const existing = inFlightByCode.get(code);
  if (existing) return existing;

  const startedAt = Date.now();
  const previousCheck = lastCheckStartedAt.get(code);
  if (
    previousCheck !== undefined &&
    startedAt - previousCheck < MIN_CHECK_GAP_MS
  ) {
    return Promise.resolve('skipped');
  }
  lastCheckStartedAt.set(code, startedAt);

  const pending = runSync(code)
    .catch((err): SyncTrailResult => {
      console.error('Live trail update failed', err);
      return 'skipped';
    })
    .finally(() => {
      inFlightByCode.delete(code);
    });

  inFlightByCode.set(code, pending);
  return pending;
}

export default syncTrailIfStale;
